"use client";

import { useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";

import { useSession } from "@/lib/auth-client";
import { dpopApi } from "@/lib/dpop-client";
import {
  captureReferralFromUrl,
  clearPendingReferralCode,
  getPendingReferralCode,
  PENDING_REFERRAL_EVENT,
} from "@/lib/pending-referral";
import { checkReferralDeposit, getReferralTerms } from "@/hooks/use-referrals";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/components/ui/use-toast";

// Codes only apply within 72h of account creation (server-enforced).
const NEW_ACCOUNT_WINDOW_MS = 72 * 60 * 60 * 1000;

async function accountCreatedAt(session: unknown): Promise<number | null> {
  try {
    const res = await dpopApi.get("/v1/users/me", session);
    if (!res.ok) return null;
    const data = await res.json();
    const raw = data?.created_at ?? data?.user?.created_at;
    const ts = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

async function ownReferralCode(session: unknown): Promise<string | null> {
  try {
    const res = await dpopApi.get("/v1/referrals/me/code", session);
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.referral_code === "string"
      ? data.referral_code.toUpperCase()
      : null;
  } catch {
    return null;
  }
}

// Captures ?join=CODE on every page, then applies the stored code the first
// time an authenticated session exists. 200 and 422 both clear the code (never
// retry a 422); transient failures keep it for the next page load.
export function PendingReferralApplier() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const inFlight = useRef(false);
  const settledFor = useRef<string | null>(null);
  const linkClaimFor = useRef<string | null>(null);

  // Capture on first load and on every client navigation.
  useEffect(() => {
    captureReferralFromUrl();
  }, [pathname]);

  useEffect(() => {
    if (status !== "authenticated" || !session?.fexrApiAccessToken) return;

    const run = async () => {
      const code = getPendingReferralCode();
      if (!code || inFlight.current || settledFor.current === code) return;
      inFlight.current = true;
      // A visitor who opened a referral one-link is also credited by the link
      // claim below; read the flag now, before that claim clears it.
      const viaLinkClaim = document.cookie
        .split("; ")
        .includes("fexr_link_claim=1");

      try {
        // Old account: don't call apply, and don't keep the code around for a
        // future account on this device.
        const createdAt = await accountCreatedAt(session);
        if (createdAt && Date.now() - createdAt > NEW_ACCOUNT_WINDOW_MS) {
          clearPendingReferralCode();
          settledFor.current = code;
          toast({
            title: "Referral codes apply to new accounts only",
          });
          return;
        }

        // Opened their own link: route to Refer friends instead.
        const own = await ownReferralCode(session);
        if (own && own === code) {
          clearPendingReferralCode();
          settledFor.current = code;
          toast({
            title: "That's your own invite link",
            description: "Share it from Refer friends.",
          });
          router.push("/dashboard/settings?tab=referrals");
          return;
        }

        const res = await dpopApi.post(
          "/v1/referrals/me/apply-code",
          { referral_code: code },
          session,
        );
        if (res.ok) {
          clearPendingReferralCode();
          settledFor.current = code;
          toast({
            title: "Referral applied",
            description: "You joined through a friend's invite.",
          });
        } else if (res.status === 422) {
          // Invalid, self-referral, too late, or already has a referrer.
          // After a link claim the usual reason is that the claim already
          // credited this same referral, so stay quiet there.
          clearPendingReferralCode();
          settledFor.current = code;
          if (!viaLinkClaim) {
            toast({
              title: "That invite couldn't be applied",
              description:
                "The code may be wrong, or this account already has a referrer.",
            });
          }
        } else {
          // 429 / 5xx: keep the code; the next page load retries.
          settledFor.current = code;
        }
      } catch {
        // Network error: keep the code for the next page load.
        settledFor.current = code;
      } finally {
        inFlight.current = false;
      }
    };

    run();
    window.addEventListener(PENDING_REFERRAL_EVENT, run);
    return () => window.removeEventListener(PENDING_REFERRAL_EVENT, run);
  }, [status, session, router]);

  // One-link: an anonymous open followed by sign-in/sign-up is bound to the
  // user once. The proofs stay in the /api/links HttpOnly cookie; the readable
  // fexr_link_claim flag only says there is something to claim. A referral
  // link's code is applied server-side by the claim itself.
  useEffect(() => {
    if (status !== "authenticated" || !session?.fexrApiAccessToken) return;
    const userId = session.user?.id ?? null;
    if (!userId || linkClaimFor.current === userId) return;
    if (!document.cookie.split("; ").includes("fexr_link_claim=1")) return;
    linkClaimFor.current = userId;

    dpopApi
      .authHeaders("POST", "/v1/journeys/link-contexts/claim", session)
      .then((auth) =>
        fetch("/api/links/claim", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-fexr-link": "1",
            ...auth,
          },
        }),
      )
      .catch(() => {
        // Transient: the next page load retries while the cookie lives.
        linkClaimFor.current = null;
      });
  }, [status, session]);

  return null;
}

/**
 * Call once after a deposit lands. Updates the joining week and the referrer's
 * count server-side, then tells a referred user where they stand. Silent for
 * users who didn't join through a referral. Never throws or blocks the caller.
 * Returns true when the free Fexr Plus perk was unlocked by this call.
 */
export async function runReferralDepositCheck(
  session: unknown,
  _opts: { quietWhenPending?: boolean } = {},
): Promise<boolean> {
  const result = await checkReferralDeposit(session);
  if (!result?.referred) return false;

  // Referral rewards are frozen: no progress or "link a wallet" nudges toward
  // a perk that can't be earned. Only confirm a perk the backend did unlock.
  if (result.wallet === null) return false;
  const terms = await getReferralTerms();

  if (result.joining_plus_unlocked) {
    toast({
      title: `Your ${terms.perk_days} days of Fexr Plus are unlocked`,
      description:
        "It doesn't renew on its own. Invite friends with your own link.",
      action: (
        <ToastAction
          altText="Open Refer friends"
          onClick={() => {
            window.location.href = "/dashboard/settings?tab=referrals";
          }}
        >
          Invite
        </ToastAction>
      ),
    });
    return true;
  }

  return false;
}
